import { type RileyMode, safeStorage } from '@patchbay/ui'
import * as React from 'react'

/**
 * Per-viewer UI state that spans screens: palette, key sheet, rail visibility and Riley's mode.
 * The rail mode is remembered per viewer (a convenience, so localStorage is fine).
 */
interface Workspace {
  paletteOpen: boolean
  setPaletteOpen: (open: boolean) => void
  keySheetOpen: boolean
  setKeySheetOpen: (open: boolean) => void
  /** The New case wizard (#758): the top bar, Ctrl+K and `g n` all open it. */
  newCaseOpen: boolean
  setNewCaseOpen: (open: boolean) => void
  railOpen: boolean
  toggleRail: () => void
  /** Open the rail if it is collapsed (a rail tab chord or `w` on a case, #273). */
  openRail: () => void
  rileyMode: RileyMode
  setRileyMode: (mode: RileyMode) => void
  /** "in C-0412 · Harbourfront Grand Hotel": the palette footer. */
  scope: { caseId: string; customer: string } | null
  setScope: (scope: { caseId: string; customer: string } | null) => void
}

const Ctx = React.createContext<Workspace | null>(null)

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [paletteOpen, setPaletteOpen] = React.useState(false)
  const [keySheetOpen, setKeySheetOpen] = React.useState(false)
  const [newCaseOpen, setNewCaseOpen] = React.useState(false)
  const [railOpen, setRailOpen] = React.useState(true)
  const [rileyMode, setMode] = React.useState<RileyMode>(() => (safeStorage.getItem('patchbay-riley-mode') === 'full' ? 'full' : 'rail'))
  const [scope, setScope] = React.useState<Workspace['scope']>(null)
  const value = React.useMemo<Workspace>(
    () => ({
      paletteOpen,
      setPaletteOpen,
      keySheetOpen,
      setKeySheetOpen,
      newCaseOpen,
      setNewCaseOpen,
      railOpen,
      toggleRail: () => setRailOpen((o) => !o),
      openRail: () => setRailOpen(true),
      rileyMode,
      setRileyMode: (m) => {
        setMode(m)
        setRailOpen(true)
        safeStorage.setItem('patchbay-riley-mode', m)
      },
      scope,
      setScope,
    }),
    [paletteOpen, keySheetOpen, newCaseOpen, railOpen, rileyMode, scope],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useWorkspace(): Workspace {
  const v = React.useContext(Ctx)
  if (!v) throw new Error('useWorkspace outside WorkspaceProvider')
  return v
}
